<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomCard,
  AtomLoadingSpinner,
  useToast,
} from "@chatfood/design-system";
import { computed, ref } from "vue";
import { useLocale } from "~/composables/use-locale";
import { LoyaltyProgramGuest } from "~/components/loyalty-program-guest";
import { LoyaltyProgramAuthenticated } from "~/components/loyalty-program-authenticated";
import { useRichCard } from "~/composables/use-rich-card";
import { useBusinessStore } from "~/stores/business";
import { useBrandingStore } from "~/stores/branding";
import { useCustomerStore } from "~/stores/customer";
import { useLoyaltyProgramStore } from "~/stores/loyaltyProgram";
import { useNavigation } from "~/composables/use-navigation";

const css = bemBuilder("page-loyalty-program");

const { t } = useLocale();
const { push } = useNavigation();
const { toastError } = useToast();

const businessStore = useBusinessStore();
const brandingStore = useBrandingStore();
const customerStore = useCustomerStore();
const loyaltyProgramStore = useLoyaltyProgramStore();

const coverImage = computed(() =>
  brandingStore.getMediaUrl("loyalty_marketing_banner"),
);

useRichCard({
  title: businessStore.name,
  ...(coverImage.value || businessStore.cover
    ? { image: coverImage.value || businessStore.cover }
    : {}),
  description: t("pages.loyalty_program.seo_description", {
    businessName: businessStore.name,
  }),
});

const isLoading = ref(true);

const isValidNativeLoyalty = () => {
  return loyaltyProgramStore.isEnabled && loyaltyProgramStore.isNative;
};

const redirectAndToastError = () => {
  toastError(t("pages.loyalty_program.not_enabled_message"));
  push("order", {});
};

const loadNativeLoyalty = async () => {
  if (loyaltyProgramStore.isLoaded) {
    if (!isValidNativeLoyalty()) {
      redirectAndToastError();
    }
    isLoading.value = false;
    return;
  }

  try {
    await loyaltyProgramStore.load({
      businessId: businessStore.id,
      salesChannel: businessStore.salesChannel,
    });

    if (!isValidNativeLoyalty()) {
      redirectAndToastError();
      return;
    }
    isLoading.value = false;
  } catch {
    redirectAndToastError();
  }
};

loadNativeLoyalty();
</script>

<template>
  <div :class="[css(), isLoading ? css('', 'loading') : '']">
    <AtomLoadingSpinner v-if="isLoading" />
    <AtomCard
      v-else
      padding="none"
      :class="[css('card'), customerStore.isLogged && css('card', 'guttered')]"
    >
      <LoyaltyProgramGuest v-if="!customerStore.isLogged" />
      <LoyaltyProgramAuthenticated v-else />
    </AtomCard>
  </div>
</template>

<style scoped lang="scss">
.page-loyalty-program {
  @media (width >= $breakpoint-sm) {
    padding: var(--spacing-2xl);
  }

  &--loading {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
  }

  &__card {
    display: flex;
    flex-direction: column;
    max-width: 640px;
    margin: 0 auto;

    &--guttered {
      padding: var(--spacing-lg);
    }
  }
}
</style>
